import React, { type ComponentProps, type PropsWithChildren } from 'react';
import { StyleSheet, TouchableOpacity } from 'react-native';

import { BodyText, Icon, theme } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const GroupOrderBagButton = (props: PropsWithChildren<Props>) => {
  const { children, icon, isDisabled, onPress } = props;

  // ─── Styles ───────────────────────────────────────────────────────────────

  const buttonStyles = [styles.button, isDisabled ? styles.buttonDisabled : undefined];
  const labelStyles = [styles.label, isDisabled ? styles.labelDisabled : undefined];

  const iconColor = isDisabled ? theme.colors.OPACITY.DARK_KALE.DARK : theme.colors.TEXT_COLOR;

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <TouchableOpacity
      accessibilityRole="button"
      activeOpacity={0.75}
      disabled={isDisabled}
      onPress={onPress}
      style={buttonStyles}
    >
      {icon ? <Icon name={icon} color={iconColor} width={22} height={22} /> : null}

      <BodyText sizeMatch={['14']} style={labelStyles}>
        {children}
      </BodyText>
    </TouchableOpacity>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  button: {
    height: 40,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing['2'],
    backgroundColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
    borderColor: theme.colors.OPACITY.TRANSPARENT,
    borderWidth: 1,
    borderRadius: theme.radius['3xLarge'],
    paddingHorizontal: theme.spacing['4'],
  },
  buttonDisabled: {
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  label: {
    color: theme.colors.TEXT_COLOR,
  },
  labelDisabled: {
    color: theme.colors.OPACITY.DARK_KALE.DARK,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  icon?: ComponentProps<typeof Icon>['name'];
  isDisabled: boolean;
  onPress: () => void;
};
